<template>
  <div class="soar-container flex flex-col gap-y-4">
    <h1 class="text-xl md:text-2xl text-primary font-medium border-b border-brand-gray-300">
      {{ $t('addFreeZoneEstablishment') }}
    </h1>

    <Form
      v-if="!isLoading"
      :initial-values="initialValues"
      :keep-values="true"
      class="flex flex-col gap-3"
      v-slot="{ resetForm, values }"
      @submit="(values) => onSubmit(values, resetForm)"
    >
      <div class="flex flex-col gap-y-4">
        <div class="flex gap-4 flex-col md:flex-row">
          <Field
            :rules="requiredRule"
            name="establishmentDepartmentId"
            v-slot="{ field, errors: errs, handleChange }"
          >
            <Dropdown
              :field="field"
              :errors="errs"
              :label="$t('EstablishmentDepartment')"
              :options="departments"
              class="w-full md:w-1/2"
              direction="flex-col"
              @on-selection="(item) => handleChange(+item?.id)"
            ></Dropdown>
          </Field>
          <div class="w-full md:w-1/2">
            <label for="EstablishmentCardNumber">
              {{ $t('EstablishmentCardNumber') }}
              <span class="text-brand-red">*</span>
            </label>
            <div class="flex gap-x-2">
              <Field
                :rules="requiredRule"
                name="establishmentNumber"
                v-slot="{ field, errors: errs }"
              >
                <TextInput
                  :field="field"
                  :errors="errs"
                  placeholder="establishmentCardNumber"
                  input-class="px-3 py-1.5"
                  label-class="w-full"
                  class="w-full md:w-2/3"
                  direction="flex-col"
                />
              </Field>
              <Field
                :rules="requiredRule"
                name="establishmentSponsorType"
                v-slot="{ field, errors: errs }"
              >
                <TextInput
                  :field="field"
                  :errors="errs"
                  placeholder="establishmentSponsorType"
                  input-class="px-3 py-1.5"
                  label-class="w-full"
                  class="w-full md:w-1/3"
                  direction="flex-col"
                  mask="##"
                />
              </Field>
              <Field
                v-if="values.establishmentDepartmentId == 8"
                :rules="requiredRule"
                name="establishmentType"
                v-slot="{ field, errors: errs }"
              >
                <TextInput
                  :field="field"
                  :errors="errs"
                  placeholder="establishmentType"
                  input-class="px-3 py-1.5"
                  label-class="w-full"
                  class="w-full md:w-1/3"
                  direction="flex-col"
                  mask="##"
                />
              </Field>
            </div>
          </div>
        </div>
        <div class="flex gap-4 flex-col md:flex-row">
          <Field :rules="requiredRule" name="sponsorUnifiedNumber" v-slot="{ field, errors: errs }">
            <TextInput
              :field="field"
              :errors="errs"
              :label="$t('SponsorUnifiedNumberWithSignature')"
              input-class="px-3 py-1.5"
              label-class="w-full"
              class="w-full md:w-1/2"
              direction="flex-col"
            />
          </Field>
          <Field :rules="validEmail" name="email" v-slot="{ field, errors: errs }">
            <TextInput
              :field="field"
              :errors="errs"
              :label="$t('Email')"
              :is-required="false"
              input-class="px-3 py-1.5"
              label-class="w-full"
              class="w-full md:w-1/2"
              direction="flex-col"
            />
          </Field>
        </div>
        <div class="flex gap-4 flex-col md:flex-row">
          <Field
            :rules="requiredRule"
            name="establishmentLicenceIssueDate"
            v-slot="{ field, errors: errs, handleChange }"
          >
            <Datepicker
              :field="field"
              :errors="errs"
              :label="$t('LicenceIssueDate')"
              :default-value="field.value"
              input-class="ps-3 py-0.5"
              class="w-full md:w-1/2"
              max-date="__now__"
              @on-change="(date) => handleChange(date)"
            >
            </Datepicker>
          </Field>
        </div>
        <div class="w-full flex justify-end">
          <button
            v-if="authStore.isFreezone"
            class="text-center rounded-md bg-primary/80 hover:bg-primary flex items-center gap-x-2 hover:scale-[1.02] active:scale-100 disabled:bg-gray-300 disabled:hover:scale-95 disabled:cursor-not-allowed text-white"
          >
            <div class="p-2 bg-primary rounded-s-md">
              <faDiskette class="w-4 fill-white"></faDiskette>
            </div>
            <span class="w-full text-center">
              {{ $t('Save') }}
            </span>
            <div class="p-0.5"></div>
          </button>

          <button
            v-else
            class="text-center rounded-md bg-primary/80 hover:bg-primary flex items-center gap-x-2 hover:scale-[1.02] active:scale-100 disabled:bg-gray-300 disabled:hover:scale-95 disabled:cursor-not-allowed text-white"
          >
            <div class="p-2 bg-primary rounded-s-md">
              <faCheck class="w-4 fill-white"></faCheck>
            </div>
            <span class="w-full text-center">
              {{ $t('Check') }}
            </span>
            <div class="p-0.5"></div>
          </button>
        </div>
        <!-- <MobileNumberPicker
          v-if="canFetchMobileNumbers(values)"
          :form="values"
          class="flex gap-4 flex-col md:flex-row"
          @onLoad="handleMobileNumbers(list)"
        /> -->
      </div>
    </Form>
  </div>
</template>

<script setup>
import faCheck from '@/assets/icons/faCheck.svg?component'
import faDiskette from '@/assets/icons/faDiskette.svg?component'
import client_lookups from '@/assets/json/clientLookups.json'
import Datepicker from '@/components/SimpleForm/Datepicker.vue'
import Dropdown from '@/components/SimpleForm/Dropdown.vue'
import TextInput from '@/components/SimpleForm/TextInput.vue'
import { isValidEmail } from '@/composables/useRuleHandler.js'
import { useAuthStore } from '@/stores/auth'
import { useLookupsStore } from '@/stores/lookups'
import { Field, Form } from 'vee-validate'
import { onMounted, ref } from 'vue'
import { useI18n } from 'vue-i18n'

const i18n = useI18n()
const authStore = useAuthStore()

const {
  mainDepartments: { dubai }
} = client_lookups

function requiredRule(value) {
  if (!value || !`${value}`.trim()) {
    return i18n.t('INVALID_REQUIRED')
  }

  return true
}

function validEmail(value) {
  if (!value || value.trim().length === 0) return true

  return (!!value && isValidEmail(value)) || 'INVALID_EMAIL'
  // return !!value && isValidEmail(value) ? true
}

const initialValues = ref()

const lookupsStore = useLookupsStore()
const departments = ref()
const isLoading = ref(true)
const showMobileList = ref(false)
const enableEstablishmentRegistrationOtp = ref(true)

onMounted(async () => {
  await fetchDepartments()
  initialValues.value = {
    establishmentDepartmentId: 17,
    establishmentNumber: 54634563,
    establishmentSponsorType: 12,
    sponsorUnifiedNumber: 124512312,
    establishmentLicenceIssueDate: '17/05/1995'
  }

  enableEstablishmentRegistrationOtp.value = authStore.isFreezone

  isLoading.value = false
})

/**
 * checks if form is complete.
 * if it's in dubai, check the establishment card number
 * with the added field
 *
 * all other fields just check the shown fields.
 */
function canFetchMobileNumbers(form) {
  const { establishmentDepartmentId } = form
  const isInDubai = establishmentDepartmentId == dubai

  if (isInDubai) {
    const {
      establishmentSponsorType,
      establishmentNumber,
      establishmentType,
      sponsorUnifiedNumber
    } = form

    return (
      [
        establishmentSponsorType,
        establishmentNumber,
        establishmentType,
        sponsorUnifiedNumber
      ].every((e) => !!e) && enableEstablishmentRegistrationOtp.value
    )
  }

  const { establishmentSponsorType, establishmentNumber, sponsorUnifiedNumber } = form

  return (
    [establishmentSponsorType, establishmentNumber, sponsorUnifiedNumber].every((e) => !!e) &&
    enableEstablishmentRegistrationOtp.value
  )
}

async function fetchDepartments() {
  departments.value = await lookupsStore.fetchLookup('/api/lookup/allDepartments')
}

async function onSubmit(form) {
  showMobileList.value = canFetchMobileNumbers(form)
}

function handleMobileNumbers(list) {}
</script>

<style lang="scss" scoped></style>
